import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box } from "@mui/material";
import { type GridRowId } from "@mui/x-data-grid-pro";
import {
  readDatalensBulkPropertyGroups,
  type DatalensBulkPropertyGroup,
} from "../utils/bulkPropertyGroups";
import { useBulkPropertyResolve } from "../hooks/useBulkPropertyResolve";
import { BulkAcquireForm } from "../components/BulkAcquireForm";
import { useBulkAcquireForm } from "../hooks/useBulkAcquireForm";
import { BulkFilterAccordion } from "../components/BulkFilterAccordion";
import { BulkSubmitSection } from "../components/BulkSubmitSection";
import {
  EMPTY_BULK_LIST_FILTER,
  filterBulkPropertyRows,
  type BulkListFilter,
} from "../utils/bulkListFilter";
import { type BulkPropertyRow } from "../types";
import { useCheckedIdsStore } from "../hooks/useCheckedIdsStore";
import { BulkLegendBar } from "../components/BulkLegendBar";
import { BulkAcquisitionGrid } from "../components/BulkAcquisitionGrid";
import {
  BulkResolveOverlay,
  BulkNoDataError,
  BulkPageLayout,
} from "../components/BulkAcquisitionStates";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useCheckedIdsSync, useRowsById } from "../hooks/useCheckedIdsSync";
import { useBulkAcquireConditions } from "../hooks/useBulkAcquireConditions";
import { useBulkCheckToggle } from "../hooks/useBulkCheckToggle";
import { useLeaveConfirmation } from "@/hooks/useLeaveConfirmation";
import { useLoadingPhase } from "@/hooks/useLoadingPhase";
import { SectionPaper } from "@/components/Paper";
import { useLogin } from "@/hooks/useLogin";
import { useUserConfig } from "@/hooks/useUserConfig";

// props（rows/onApply）が変わらない限りチェック操作の再レンダリングを受けないようにする
const MemoBulkFilterAccordion = React.memo(BulkFilterAccordion);

/**
 * DataLens から受け渡された複数物件コードの一括取得画面。
 * sessionStorage のコードリストを読み出しと同時に消費し（リロード・再訪では復元しない）、
 * 照合 API で所在情報を解決して一覧表示する。
 * 地番または家屋番号は未取得物件では null で返るため＊＊＊表示にする
 */
export const PropertyCodeBulkAcquisition: React.FC = () => {
  // undefined は読み出し前、null は受け渡しデータなし
  const [groups, setGroups] = useState<
    DatalensBulkPropertyGroup[] | null | undefined
  >(undefined);
  // 読み出しは消費（1回限り）のため、StrictMode の二重実行で2回目が
  // データ不在扱いにならないようガードする
  const consumedRef = useRef(false);

  // チェック状態は外部ストアで持つ（チェック1回での列定義再生成・全セル再描画を避ける。
  // チェック変更の購読は BulkSubmitSection 側に閉じる）
  const checkedStore = useCheckedIdsStore();

  const { isLoggedIn } = useLogin();
  // 値の取り出しは依頼実行（確認モーダル）側で行うため、フォームは画面側で生成して渡す
  const acquireForm = useBulkAcquireForm();

  // 経過日数の初期値はユーザー設定から反映する（既存の /acquiremultiplebooks と同じ挙動）
  useUserConfig({
    onUserSettingsLoaded: (data) => {
      acquireForm.setValue("pastDays", data.pictureRequestPastDays);
    },
    // 一度でも入力があったら更新しないようにする
    enableCallbackOnRefetch: !acquireForm.formState.isDirty,
  });

  useEffect(() => {
    // 未ログイン時は読み出さない（ログイン後の再マウントで読み出す）
    if (!isLoggedIn || consumedRef.current) {
      return;
    }
    consumedRef.current = true;
    setGroups(readDatalensBulkPropertyGroups());
  }, [isLoggedIn]);

  const { resolveResult, isLoading, error, progress } = useBulkPropertyResolve(
    groups ?? null
  );

  // 照合成功の直後は、チェックマーク表示→フェードアウトの余韻を見せてから一覧に切り替える
  const loadingPhase = useLoadingPhase(
    isLoading,
    error == null && resolveResult != null
  );

  const { dialog: leaveConfirmationDialog } = useLeaveConfirmation(
    isLoading || (resolveResult != null && resolveResult.properties.length > 0),
    "ページを離れるとDataLensからやり直しになります。ページを離れますか？"
  );

  // 5万件規模の行構築をチェック操作ごとに繰り返さないようメモ化する
  const rows = useMemo((): BulkPropertyRow[] | null => {
    if (resolveResult == null) {
      return null;
    }
    return resolveResult.properties.map((property, index) => ({
      id: index + 1,
      ...property,
    }));
  }, [resolveResult]);

  const { policy, committedPastDays, pastDaysFocusProps, excludeAcquired } =
    useBulkAcquireConditions(acquireForm);

  const rowsById = useRowsById(rows);
  useCheckedIdsSync(
    checkedStore,
    rowsById,
    policy,
    committedPastDays,
    excludeAcquired
  );

  // 適用中の絞り込み。事項種別の判定は条件を変えた時点のチェック状態で行うため、
  // その時点のチェック集合スナップショットを条件と一緒に保持する
  // （絞り込み中のチェック操作・チェック解除で表示行が黙って変わるのを防ぐ）
  const [applied, setApplied] = useState<{
    filter: BulkListFilter;
    checkedIdsByField: Record<string, Set<GridRowId>>;
  }>({ filter: EMPTY_BULK_LIST_FILTER, checkedIdsByField: {} });

  // 表示対象＝絞り込み結果。チェックは絞り込みをまたいで維持され、
  // 実行対象は非表示行を含む全チェック行（件数はカウンタと確認ダイアログで見せる）
  const filteredRows = useMemo(
    () =>
      rows == null
        ? null
        : filterBulkPropertyRows(
            rows,
            applied.filter,
            applied.checkedIdsByField
          ),
    [rows, applied]
  );

  const visibleRowIds = useMemo(
    () =>
      filteredRows == null
        ? null
        : new Set<GridRowId>(filteredRows.map((row) => row.id)),
    [filteredRows]
  );

  // 絞り込みを変えながらチェックを積み上げてまとめて依頼できるよう、
  // 結果セット外の行のチェックも解除せず維持する
  const handleApplyFilter = useCallback(
    (nextFilter: BulkListFilter): void => {
      setApplied({
        filter: nextFilter,
        checkedIdsByField: checkedStore.getAll(),
      });
    },
    [checkedStore]
  );

  const { onToggle, onColumnHeaderDoubleClick } = useBulkCheckToggle({
    store: checkedStore,
    filteredRows,
    visibleRowIds,
    policy,
    pastDays: committedPastDays,
    excludeAcquired,
  });

  // 読み出し・照合が終わるまでは見出しとローディング表示だけを先に描画する
  // （5万件規模ではデータ処理に時間がかかるため、画面全体を待たせない）
  if (groups === undefined || isLoading) {
    return (
      <>
        <BulkPageLayout>
          <Box sx={{ position: "relative", minHeight: 240 }}>
            <BulkResolveOverlay
              processedCodes={progress.processedCodes}
              totalCodes={progress.totalCodes}
            />
          </Box>
        </BulkPageLayout>
        {leaveConfirmationDialog}
      </>
    );
  }

  // 受け渡しデータなし（sessionStorage 不在・形式不正・TTL超過）、照合エラー、
  // 全件不在はいずれもやり直し案内を出して中断する
  if (groups === null || error != null || rows == null || rows.length === 0) {
    return <BulkNoDataError />;
  }

  return (
    <BulkPageLayout>
      {/* 余韻中は一覧の上に完了表示を重ね、フェードアウトで一覧とクロスフェードさせる */}
      <Box sx={{ position: "relative" }}>
        {loadingPhase === "exit" && (
          <BulkResolveOverlay
            processedCodes={progress.processedCodes}
            totalCodes={progress.totalCodes}
            finished
          />
        )}
        <Box>
          {resolveResult != null && resolveResult.unresolvedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {formatNumberWithComma(resolveResult.unresolvedCount)}
              件の物件はデータが見つからなかったため表示していません
            </Alert>
          )}
          <BulkAcquireForm
            form={acquireForm}
            pastDaysFocusProps={pastDaysFocusProps}
          />
          <MemoBulkFilterAccordion rows={rows} onApply={handleApplyFilter} />
          <BulkLegendBar />
          {/* グリッドから実行ボタンまでを1枚のカードに収める（既存の一括取得画面と同じ構成） */}
          <SectionPaper>
            <BulkAcquisitionGrid
              store={checkedStore}
              rows={rows}
              filteredRows={filteredRows ?? []}
              visibleRowIds={visibleRowIds}
              policy={policy}
              pastDays={committedPastDays}
              excludeAcquired={excludeAcquired}
              onToggle={onToggle}
              onColumnHeaderDoubleClick={onColumnHeaderDoubleClick}
            />
            <BulkSubmitSection
              store={checkedStore}
              rows={rows}
              visibleRowIds={visibleRowIds}
              form={acquireForm}
            />
          </SectionPaper>
        </Box>
      </Box>
      {leaveConfirmationDialog}
    </BulkPageLayout>
  );
};
